/**
 * Zod-schema's voor de automatische dagplanning (Epic D-1 — Fundament,
 * Story D-1.1).
 *
 * Alleen de instellingen-input van Epic D-1 staat hier; de generator-,
 * voorstel- en momentopname-routes voegen hun eigen schema's toe in D-2..D-5.
 * Dezelfde split-conventie als `schemas/planning.ts`: het vocabulaire staat in
 * `types/dagplanning.ts`, de validatie hier.
 */
import { z } from 'zod';
import { PLANNING_TASK_ACTIVITIES } from './planning.js';
/**
 * De bovengrens van een MySQL `int` met teken. `planning_capacity.daily_score`
 * is zo'n kolom; alles daarboven komt als databasefout terug (1264, "Out of
 * range value") in plaats van als validatiemelding. Dit is de KOLOMGRENS en
 * bewust geen bedrijfsregel — welk dagcijfer realistisch is, is een keuze van
 * de planningsbeheerder en hoort niet in een zod-schema.
 */
export const MYSQL_INT_MAX = 2_147_483_647;
/**
 * FR-6 — het dagcijfer van een medewerker.
 *
 * Een geheel getal groter dan nul. Nul is bewust ongeldig: een medewerker die
 * niets aankan hoort uit de pool te worden gehaald (FR-10), niet op dagcijfer
 * nul te staan — anders verschijnt hij als lege cel op het bord terwijl de
 * generator hem nooit werk kan geven.
 *
 * De bovengrens is de kolomgrens (zie `MYSQL_INT_MAX`). Zonder die grens
 * bereikt `3000000000` de database en komt de fout terug als een 500 in plaats
 * van als een leesbare validatiemelding op het veld.
 */
export const updatePlanningCapacitySchema = z.object({
    dailyScore: z.number().int().positive().max(MYSQL_INT_MAX),
});
/**
 * FR-7 — de organisatiebrede wegingsfactor van één activiteit.
 *
 * `weight` is een breukgetal (`double` in de database) en moet groter zijn dan
 * nul: een factor van nul maakt de bijbehorende taaksoort onzichtbaar in de
 * vensterberekening van AD-31 in plaats van hem lager te wegen.
 *
 * `.finite()` staat er omdat `z.number().positive()` `Infinity` toelaat —
 * nagemeten. Een oneindige factor haalt AD-31's venster (`Σ dagcijfer ×
 * wegingsfactor`) onderuit en komt bovendien als `null` in een `double`-kolom
 * terecht. De bovengrens is die van `double` zelf, niet een verzonnen maximum:
 * hoe zwaar valideren mag wegen ten opzichte van vastleggen is een keuze van
 * de planningsbeheerder.
 */
export const updateActivityWeightSchema = z.object({
    activity: z.enum(PLANNING_TASK_ACTIVITIES),
    weight: z.number().finite().positive(),
});
/**
 * FR-10 — de handmatige planbaarheidsschakelaar.
 *
 * De reden is verplicht, ook bij het terugzetten op planbaar: de stand moet
 * herleidbaar zijn tot wie hem zette en waarom. `.trim()` vóór `.min(1)`
 * zorgt dat een reden van alleen witruimte net zo hard wordt geweigerd als een
 * lege — anders is de eis met één spatie te omzeilen.
 */
export const setPlanningPoolOverrideSchema = z.object({
    isPlanbaar: z.boolean(),
    reason: z.string().trim().min(1).max(255),
});
/**
 * Story D-1.4 — de queryparameter van `GET /api/v1/planning/pool?date=`.
 * Zelfde strikte `YYYY-MM-DD`-vorm als `planningBoardQuerySchema` (AD-17:
 * zod `safeParse` + `{error, code, details?}` op elke nieuwe planningroute).
 *
 * De `.refine` eist een BESTAANDE kalenderdag: `2026-02-31` haalt de regex
 * maar rolt in `Date.UTC` door naar 2 maart, waardoor de datumsleutels
 * verderop misgrijpen en de route stilzwijgend "iedereen planbaar" zou
 * teruggeven in plaats van een 400. Round-trip via `Date.UTC` → ISO moet de
 * invoer exact teruggeven.
 *
 * De NaN-guard is nodig omdat zod (v3) refinements óók draait wanneer de
 * regex-check al faalde: zonder guard gooit `toISOString()` op een
 * ongeldige datum (bijv. `date=vandaag`) een RangeError en wordt de 400 een
 * 500.
 */
export const planningPoolQuerySchema = z.object({
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (verwacht YYYY-MM-DD)')
        .refine((value) => {
        const [y, m, d] = value.split('-').map(Number);
        const timestamp = Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1);
        if (!Number.isFinite(timestamp)) {
            return false;
        }
        return new Date(timestamp).toISOString().slice(0, 10) === value;
    }, 'Ongeldige kalenderdatum'),
});
/**
 * FR-8 — de queryparameters van `GET /api/v1/planning/capacity/per-dagdeel`.
 *
 * Staat hier en niet als handgeschreven controle in de route, omdat AD-17 het
 * letterlijk voorschrijft: "alle nieuwe routes onder `/api/v1/planning/*`
 * (registratie in `app.ts`, `requireAuth` + zod `safeParse` + `{error, code,
 * details?}` — AD-9)". Zonder zod komt er een 400 zónder `details` terug en
 * weet de aanroeper niet wélke parameter fout was.
 *
 * `employeeId` wordt bewust NIET met `z.coerce.number()` gelezen: coercion
 * accepteert `' 7 '`, `'7e0'` en `'0x7'`. De regex is dezelfde strikte
 * `^\d+$`-vorm als `parseIdParam` in de bestaande routebestanden, met dezelfde
 * kolomgrens erachter.
 */
export const capacityPerDayPartQuerySchema = z.object({
    employeeId: z
        .string()
        .regex(/^\d+$/, 'employeeId moet een geheel getal zijn')
        .transform(Number)
        .pipe(z.number().int().positive().max(MYSQL_INT_MAX)),
    activity: z.enum(PLANNING_TASK_ACTIVITIES),
});
//# sourceMappingURL=dagplanning.js.map